import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { IdCard, Loader2, Trash2, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/client-id-card`;

// Drop the client's ID (front) once — it's compressed, saved to the client, and placed next to the
// signature on every dispute letter generated for them.
export default function ClientIdDrop({ clientName }) {
  const [typed, setTyped] = useState('');
  const name = ((clientName || '').trim() || typed).trim();
  const [meta, setMeta] = useState(null);
  const [thumb, setThumb] = useState('');
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const input = useRef(null);

  const loadThumb = async () => {
    try {
      const r = await axios.get(`${API}/image`, { params: { client_name: name }, responseType: 'blob' });
      setThumb((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(r.data); });
    } catch { setThumb(''); }
  };
  useEffect(() => {
    if (!name) { setMeta(null); setThumb(''); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(API, { params: { client_name: name } });
        setMeta(data);
        if (data.exists) loadThumb(); else setThumb('');
      } catch { setMeta(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [name]);

  const upload = async (file) => {
    if (!file) return;
    if (!name) { toast.error("Type the client's name first"); return; }
    if (!/^image\//.test(file.type) && !/\.(jpe?g|png|webp)$/i.test(file.name)) { toast.error('Drop a photo of the ID (JPG, PNG or WEBP)'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('client_name', name);
      const { data } = await axios.post(API, fd, { timeout: 120000 });
      setMeta(data);
      await loadThumb();
      toast.success(`ID saved for ${name} (${Math.round(data.size / 1024)} KB) — it will sit next to the signature on every letter`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the ID'); }
    setBusy(false);
  };
  const remove = async () => {
    await axios.delete(API, { params: { client_name: name } });
    setMeta({ exists: false });
    setThumb('');
    toast.success('ID removed — letters will no longer include it');
  };

  return (
    <div className="mt-4 rounded-xl border border-sky-500/30 bg-sky-500/5 p-3" data-testid="client-id-drop">
      <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-sky-200"><IdCard className="h-4 w-4" />Client ID (front) for dispute letters</p>
      <div className="flex flex-wrap items-center gap-3">
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
          onClick={() => input.current?.click()}
          className={`flex min-w-[220px] flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-sm transition-colors ${drag ? 'border-sky-400 bg-sky-500/10' : 'border-white/10 hover:border-sky-400/50'}`}
          data-testid="client-id-dropzone"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin text-sky-300" /> : <Upload className="h-5 w-5 text-slate-400" />}
          <span className="text-slate-300">{meta?.exists ? 'Drop a new ID to replace it' : 'Drop the ID here, or click to browse'}</span>
          <input ref={input} type="file" accept="image/jpeg,image/png,image/webp,image/*" hidden onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} data-testid="client-id-input" />
        </div>
        {meta?.exists && thumb && (
          <div className="flex items-center gap-2" data-testid="client-id-saved">
            <img src={thumb} alt="Client ID" className="h-16 w-auto rounded border border-white/20" data-testid="client-id-thumb" />
            <div className="text-[11px] text-slate-400">
              <p className="text-emerald-300">Saved · {Math.round((meta.size || 0) / 1024)} KB</p>
              <button type="button" onClick={remove} className="mt-1 flex items-center gap-1 text-rose-300 hover:underline" data-testid="client-id-remove"><Trash2 className="h-3 w-3" />Remove</button>
            </div>
          </div>
        )}
      </div>
      {!(clientName || '').trim() && (
        <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Client's full name (same as on the letters)"
          className="mt-2 w-full rounded-md border border-white/10 bg-black/30 px-2 py-1.5 text-sm text-white placeholder:text-slate-500" data-testid="client-id-name" />
      )}
    </div>
  );
}
